import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { CadastroService } from '../../../../../services/cadastro.service';
import { Fornecedor } from '../../../../../models/cadastro.model';
import { NgxMaskDirective } from 'ngx-mask';
import { CommonModule } from '@angular/common';
import { ErrorModel } from '../../../../../models/error.model';

/** 26 estados + Distrito Federal. A coluna no banco aceita apenas 2 caracteres. */
const UFS_BRASIL: string[] = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA',
  'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
];

/**
 * Rede de seguranca para valores que nao vieram do select — registros antigos
 * carregados via patchValue poderiam reenviar um estado invalido no save.
 */
function ufBrasilValidator(control: AbstractControl): ValidationErrors | null {
  const valor = (control.value ?? '').toString().trim();
  if (!valor) {
    return null;
  }
  return UFS_BRASIL.includes(valor.toUpperCase()) ? null : { ufInvalida: true };
}

@Component({
  selector: 'app-fornecedores-manutencao',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NgxMaskDirective],
  templateUrl: './fornecedores-manutencao.component.html',
  styleUrls: ['./fornecedores-manutencao.component.css']
})
export class FornecedoresManutencaoComponent implements OnInit {
  fornecedorForm!: FormGroup;
  fornecedorId: number | null = null;
  isEditMode = false;
  readonly ufs = UFS_BRASIL;

  constructor(
    private fb: FormBuilder,
    private cadastroService: CadastroService,
    private router: Router,
    private route: ActivatedRoute,
    private toastr: ToastrService
  ) { }

  ngOnInit(): void {
    this.fornecedorForm = this.fb.group({
      razaoSocial: ['', Validators.required],
      nomeFantasia: [''],
      cnpj: ['', Validators.required],
      inscricaoEstadual: [''],
      inscricaoMunicipal: [''],
      endereco: [''],
      cidade: [''],
      estado: ['', ufBrasilValidator],
      cep: [''],
      numeroPredial: [''],
      telefone: [''],
      email: ['', [Validators.email]],
      site: [''],
      observacoes: ['']
    });

    this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      if (id) {
        this.isEditMode = true;
        this.fornecedorId = +id;
        this.loadFornecedor(this.fornecedorId);
      }
    });
  }

  loadFornecedor(id: number): void {
    this.cadastroService.getFornecedorById(id).subscribe(fornecedor => {
      this.fornecedorForm.patchValue(fornecedor);

      // Registros cadastrados antes do dropdown podem trazer um estado fora da lista
      // de UFs. O select nao teria opcao correspondente e exibiria vazio, mas o valor
      // invalido continuaria no form e seria reenviado no save.
      const estadoSalvo = (fornecedor.estado ?? '').toString().trim().toUpperCase();
      if (estadoSalvo && !UFS_BRASIL.includes(estadoSalvo)) {
        this.fornecedorForm.get('estado')?.setValue('');
        this.toastr.warning(
          `O estado "${fornecedor.estado}" não é uma UF válida. Selecione a UF correta antes de salvar.`,
          'Estado inválido'
        );
        return;
      }

      this.fornecedorForm.get('estado')?.setValue(estadoSalvo);
    });
  }

  salvarFornecedor(): void {
    if (this.fornecedorForm.valid) {
      const fornecedorData: Fornecedor = { id: this.fornecedorId || 0, ...this.fornecedorForm.value };

      const operation = this.isEditMode
        ? this.cadastroService.updateFornecedor(fornecedorData)
        : this.cadastroService.createFornecedor(fornecedorData);

      operation.subscribe({
        next: () => {
          this.toastr.success(`Fornecedor ${this.isEditMode ? 'atualizado' : 'cadastrado'} com sucesso!`);
          this.router.navigate(['/home', 'cadastro-fornecedor']);
        },
        error: (error) => {
          const erro: ErrorModel = error.error;
          this.toastr.error(erro.message);
        }
      });
    } else {
      this.fornecedorForm.markAllAsTouched();

      if (this.fornecedorForm.get('estado')?.hasError('ufInvalida')) {
        this.toastr.error('Selecione uma UF válida no campo Estado.');
        return;
      }

      this.toastr.error('Por favor, preencha todos os campos obrigatórios.');
    }
  }

  cancelar(): void {
    this.router.navigate(['/home', 'cadastro-fornecedor']);
  }
}
